import React, { forwardRef, useRef } from 'react';
import { Transition } from 'react-transition-group';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Text, Input, InputBox, Icon } from '../../atoms';

const TextFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 14px;
`;

const AnimatedErrorMessage = styled(Text)`
  transition: 100ms ease-out;
  position: relative;
  margin-left: 12px;
  margin-top: 5px;

  transform: translateY(${({ state }) => (state === 'entering' || state === 'entered' ? 0 : -5)}px);
  opacity: ${({ state }) => (state === 'entering' || state === 'entered' ? 1 : 0)};
`;

const IconLabel = styled.label`
  display: inline-flex;
`;

const StyledInputBox = styled(InputBox)`
  ${({ multiline }) =>
    multiline &&
    `
      padding: 12px;
      height: auto;
      min-height: 40px;
    `}
`;

const TextField = forwardRef(
  (
    {
      autoComplete = null,
      className = null,
      error = null,
      icon = null,
      max = null,
      min = null,
      onBlur = null,
      onFocus = null,
      onKeyDown = null,
      placeholder = null,
      type = 'text',
      inputAs = 'input',
      name,
      onChange
    },
    forwardedRef
  ) => {
    const errorRef = useRef(null);
    return (
      <TextFieldContainer className={className}>
        <StyledInputBox multiline={inputAs === 'textarea'}>
          {icon && (
            <IconLabel htmlFor={name}>
              <Icon size={24} name={icon} color='var(--content-subtitle)' />
            </IconLabel>
          )}
          <Input
            as={inputAs}
            autoComplete={autoComplete}
            id={name}
            max={max}
            min={min}
            name={name}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={forwardedRef}
            type={type}
            rows={inputAs === 'textarea' ? 4 : undefined}
          />
        </StyledInputBox>
        <Transition nodeRef={errorRef} in={!!error} timeout={500}>
          {(state) => (
            <AnimatedErrorMessage
              ref={errorRef}
              state={state}
              $small
              $color='var(--critical-default)'
            >
              {error}
            </AnimatedErrorMessage>
          )}
        </Transition>
      </TextFieldContainer>
    );
  }
);

TextField.propTypes = {
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  inputAs: PropTypes.string
};

export { TextField };
